import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import PostCard from "./JobPostCard/PostCard";  
import SideFilter from '../../Shared/Filters/SideFilter/SideFilter';
import FilterOrderPagination from '../../Shared/FilterOrderPagination/FilterOrderPagination';
import './jobPosts.css';
import useFetchAllJobs from '../../../hooks/jobs/useFetchAllJobs';
import JobSearchBar from '../JobSearchBar/JobSearchBar';
import { filterIcon, refreshIcon } from '../../../utils/icons';
import {filterOptionsJobPosts} from '../../../helpers/filterOptions';
import Loading from '../../Shared/Loading/Loading';

const JobPosts = () => {
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [currentPage, setCurrentPage] = useState(1);
  const [initialSearchTerm, setInitialSearchTerm] = useState('');
  const [isSidebarFilterOpen, setIsSidebarFilterOpen] = useState(false);
  const [clearSearchTrigger, setClearSearchTrigger] = useState(false);
  const [orderBy, setOrderBy] = useState('');
  const filterRef = useRef(null);
  const memoizedFilters = useMemo(() => filters, [filters]);
  const { posts, isLoading, error, totalPages, totalJobs } = useFetchAllJobs({
    filters,
    searchTerm: debouncedSearchTerm, 
    page: currentPage,
    limit: 10,
    orderBy
  });
  
  const location = useLocation();
  const [isShowingLoader, setIsShowingLoader] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 100);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const search = searchParams.get('search') || '';
    setSearchTerm(search); 
    setInitialSearchTerm(search); // Set initial search term from URL
    setCurrentPage(1); 
  }, [location.search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsSidebarFilterOpen(false);
      }
    };

    if (isSidebarFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSidebarFilterOpen]);

  useEffect(() => {
    if (isLoading) {
      setIsShowingLoader(true);
    } else {
      const timer = setTimeout(() => {
        setIsShowingLoader(false);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  const clearFilters = () => {
    setFilters({});
    setCurrentPage(1); 
    setSearchTerm('');
    setClearSearchTrigger((prev) => !prev); 
  }  

  const handleFilterChange = (newFilters) => {
    const cleanedFilters = Object.fromEntries(
      Object.entries(newFilters).filter(([key, value]) => value !== null && value !== '')
    );

    setFilters(cleanedFilters);
    setCurrentPage(1);
  };

  const handleSearchResultSelect = (position) => {
    setIsShowingLoader(true);
    setSearchTerm(position); 
    setCurrentPage(1); 
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const toggleSidebar = () => {
    setIsSidebarFilterOpen(!isSidebarFilterOpen);
  };

  const handleOrderChange = useCallback((newOrder) => {
    setOrderBy(newOrder);  
    setCurrentPage(1);  
  }, []);

  if (isLoading || isShowingLoader) {
    return (
      <Loading text={'trabajos'}/>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error loading jobs: {error}</p>
      </div>
    );
  }

  return (
    <>
      <JobSearchBar 
        onSelectResult={handleSearchResultSelect} 
        initialSearchTerm={initialSearchTerm}
        onClearSearch={clearSearchTrigger} 
      />
      <div className="jobs-posts__list-filter">
        <div ref={filterRef}  className={`side-filter-container ${isSidebarFilterOpen ? 'open' : ''}`}>
          <button onClick={clearFilters} className="job-posts__clear-filters-button">
            {refreshIcon}
          </button>

          <SideFilter
            filters={filterOptionsJobPosts}
            selectedFilters={filters}
            onFilterChange={handleFilterChange}
          />
        </div>
        <div className="jobs-posts__pagination-posts-container">
          <div className="job-post__icons-container">
            <button className="job-post__filter-icon" onClick={toggleSidebar}>{filterIcon}</button>
            <button onClick={clearFilters} className="job-posts__clear-filters-button-mobile">
              {refreshIcon}
            </button>
          </div>
        
          <FilterOrderPagination
            currentPage={currentPage}
            totalPages={totalPages} 
            onPageChange={handlePageChange} 
            data={posts || []}  
            itemsPerPage={10}  
            totalItems={totalJobs}
            onOrderChange={handleOrderChange}
            type={'job'}
            orderBy={orderBy}
          />
          <div className="job-posts__container">
            {posts && posts.length > 0 ? (
              posts.map((job, index) => (
                <PostCard key={job.id || index} data={job} type="job" />
              ))
            ) : (
              <p>No job posts available</p>
            )}
          </div>
        </div> 
      </div>
    </>
  );
};

export default JobPosts;
